import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Upload, RefreshCw, Lock, AlertTriangle, CheckCircle2, Database, Link2, Ruler } from "lucide-react";
import api, { apiError } from "../../lib/apiClient";
import { SleepwearReadiness } from "./SleepwearReadiness";

const fmtCode = (c) => String(c || "").replace(/_/g, " ").toLowerCase();

function Stat({ label, value, sub, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-line p-4" data-testid={testid}>
      <p className="text-[11px] font-bold uppercase tracking-wider text-ink-muted">{label}</p>
      <p className="font-heading font-black text-2xl text-ink mt-1">{value}</p>
      {sub && <p className="text-xs text-ink-soft mt-0.5">{sub}</p>}
    </div>
  );
}

function CategoryTable({ byCategory }) {
  return (
    <table className="w-full text-sm" data-testid="catalog-category-table">
      <thead><tr className="text-left text-[11px] uppercase tracking-wider text-ink-muted">
        <th className="py-2">Product category</th><th>Target</th><th>Public</th><th>Candidates</th><th>Blocked</th><th>Gap</th></tr></thead>
      <tbody>
        {Object.entries(byCategory || {}).map(([k, v]) => (
          <tr key={k} className="border-t border-line" data-testid={`catalog-category-row-${k}`}>
            <td className="py-2 font-semibold text-ink">{v.label}</td><td>{v.target}</td><td className="text-success font-bold">{v.public}</td>
            <td>{v.candidate_drafts}</td><td className="text-brand">{v.blocked_drafts}</td><td className="font-bold">{v.gap}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const MODE_LABEL = { exact_match: "Exact Match", coordinating: "Coordinating", complete_the_look: "Complete the Look" };

function CollectionGrid({ byOccasion, byMode }) {
  return (
    <div className="overflow-x-auto" data-testid="catalog-collection-grid">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-[11px] uppercase tracking-wider text-ink-muted">
          <th className="py-2">Occasion (3 each)</th>{Object.keys(MODE_LABEL).map((m) => <th key={m}>{MODE_LABEL[m]} ({byMode[m].public}/{byMode[m].target})</th>)}<th>Public</th></tr></thead>
        <tbody>
          {Object.entries(byOccasion || {}).map(([occ, v]) => (
            <tr key={occ} className="border-t border-line" data-testid={`catalog-collection-row-${occ}`}>
              <td className="py-2 font-semibold text-ink">{v.label}</td>
              {Object.keys(MODE_LABEL).map((m) => (
                <td key={m} className={v.modes[m] ? "text-success font-bold" : "text-ink-muted"}>{v.modes[m] ? `${v.modes[m]} ✓` : "—"}</td>
              ))}
              <td className="font-bold">{v.public} / {v.target}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Adapters({ adapters }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid="catalog-adapters">
      {(adapters || []).map((a) => (
        <div key={a.key} className="bg-white rounded-2xl border border-line p-4" data-testid={`catalog-adapter-${a.key}`}>
          <div className="flex items-center justify-between gap-2">
            <p className="font-bold text-ink">{a.label}</p>
            <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full ${a.enabled ? "bg-success/10 text-success" : "bg-cream text-ink-soft border border-line"}`} data-testid={`catalog-adapter-status-${a.key}`}>
              {a.enabled ? <CheckCircle2 size={12} /> : <Lock size={12} />} {a.enabled ? "Enabled" : "Disabled"}
            </span>
          </div>
          <p className="text-xs text-ink-soft mt-1">{a.program}</p>
          {!a.enabled && <p className="text-xs text-ink-muted mt-2">{a.reason}</p>}
          <p className="text-[11px] text-ink-muted mt-2 break-all">Required: {a.required_env.join(", ")}</p>
          <p className="text-[11px] mt-1 text-ink-muted">Affiliate tracking: {a.affiliate.approved ? "approved template configured" : "not approved — no tag will be invented"}</p>
        </div>
      ))}
    </div>
  );
}

function CsvImport({ onDone }) {
  const [dryRun, setDryRun] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post(`/admin/catalog/import/csv?dry_run=${dryRun}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`${data.received} rows checked — ${data.ready_for_review} ready for review, ${data.blocked} blocked${dryRun ? " (dry run, nothing saved)" : ""}.`);
      onDone && onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-2xl border border-line p-4 space-y-3" data-testid="catalog-csv-import">
      <div className="flex flex-wrap items-center gap-3">
        <label className="inline-flex items-center gap-2 h-11 px-4 rounded-full bg-brand text-white font-bold text-sm cursor-pointer hover:bg-brand-hover transition-all">
          <Upload size={16} /> {busy ? "Checking…" : "Upload CSV"}
          <input type="file" accept=".csv,text/csv" className="hidden" disabled={busy} data-testid="catalog-csv-input" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(f); }} />
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-ink-soft"><input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} data-testid="catalog-dry-run" className="accent-brand" /> Dry run (validate only)</label>
        <a href={`${api.defaults.baseURL}/admin/catalog/template.csv`} onClick={async (e) => { e.preventDefault(); const { data } = await api.get("/admin/catalog/template.csv"); const b = new Blob([data], { type: "text/csv" }); const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = "siblingo_catalog_import_template.csv"; a.click(); }} className="text-sm font-semibold text-brand underline" data-testid="catalog-template-link">Download CSV template</a>
      </div>
      <p className="text-xs text-ink-muted">Every row lands as a hidden, draft, unapproved product. Rows missing an exact SKU, canonical retailer URL, fresh price/stock, skin-free-reviewed exact-product image, permitted image provenance, in-stock variants or an approved size chart are blocked automatically. Feed affiliate URLs are ignored — tracked links are generated only after affiliate approval.</p>
      {result && (
        <div className="overflow-x-auto" data-testid="catalog-import-result">
          <p className="text-sm font-bold text-ink mb-2">{result.received} rows · {result.ready_for_review} ready for admin review · {result.blocked} blocked{result.dry_run ? " · dry run" : ""}</p>
          <table className="w-full text-xs">
            <thead><tr className="text-left text-ink-muted uppercase tracking-wider"><th className="py-1">#</th><th>Retailer</th><th>SKU</th><th>Title</th><th>Status</th><th>Blocks</th></tr></thead>
            <tbody>
              {result.rows.map((r) => (
                <tr key={r.row} className="border-t border-line align-top" data-testid={`catalog-import-row-${r.row}`}>
                  <td className="py-1.5">{r.row}</td><td>{r.retailer}</td><td className="font-mono">{r.sku || "—"}</td><td>{r.title}</td>
                  <td className={r.status === "blocked" ? "text-brand font-bold" : "text-success font-bold"}>{r.status === "blocked" ? "Blocked" : "Ready for review"}</td>
                  <td>{r.blocks.map((b) => <span key={b.code} title={b.detail} className="inline-block mr-1 mb-1 px-2 py-0.5 rounded-full bg-cream border border-line capitalize">{fmtCode(b.code)}</span>)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function CatalogImport() {
  const [rd, setRd] = useState(null);
  const [imports, setImports] = useState([]);
  const load = useCallback(async () => {
    try {
      const [{ data: r }, { data: im }] = await Promise.all([api.get("/admin/catalog/readiness"), api.get("/admin/catalog/imports")]);
      setRd(r); setImports(im || []);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!rd) return <div className="bg-white rounded-3xl border border-line h-64 animate-pulse" data-testid="catalog-loading" />;
  const p = rd.products, c = rd.collections;
  return (
    <div className="space-y-6 fade-up" data-testid="catalog-import-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading font-black text-2xl text-ink flex items-center gap-2"><Database className="text-brand" size={22} /> Catalog import &amp; readiness</h2>
          <p className="text-sm text-ink-soft">70-product / 21-collection plan. Nothing is published from here — slots fill only with verified, individually approved products.</p>
        </div>
        <button onClick={load} data-testid="catalog-refresh" className="h-10 px-4 rounded-full border border-line bg-white text-sm font-semibold text-ink-soft hover:border-brand/30 inline-flex items-center gap-1.5"><RefreshCw size={14} /> Refresh</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Distinct public listings" value={`${p.distinct_public} / ${rd.targets.products}`} sub={`${p.public_placements} placements · ${p.distinct_public_skus} variant SKUs`} testid="catalog-stat-products" />
        <Stat label="Public collections" value={`${c.public} / ${rd.targets.collections}`} sub={`${c.unfilled} slots unfilled`} testid="catalog-stat-collections" />
        <Stat label="Import drafts" value={p.candidate_drafts + p.blocked_drafts} sub={`${p.candidate_drafts} ready for review · ${p.blocked_drafts} blocked`} testid="catalog-stat-drafts" />
        <Stat label="Public audit (checks only)" value={rd.public_audit.failing === 0 ? `${rd.public_audit.passing}/${rd.public_audit.public_placements} checks pass` : `${rd.public_audit.failing} failing`} sub={`Checks populated placements only — NOT launch readiness: ${p.distinct_public} of 70 public listings populated, ${c.public - c.unfilled} of ${c.public} public collections filled (${c.unfilled} unfilled)`} testid="catalog-stat-audit" />
      </div>

      <section className="bg-white rounded-2xl border border-line p-4">
        <h3 className="font-bold text-ink mb-2">70 distinct products by category</h3>
        <CategoryTable byCategory={p.by_category} />
      </section>
      <section className="bg-white rounded-2xl border border-line p-4">
        <h3 className="font-bold text-ink mb-2">21 collections — 3 per occasion (Exact / Coordinating / Complete the Look)</h3>
        <CollectionGrid byOccasion={c.by_occasion} byMode={c.by_mode} />
      </section>
      {(rd.eligibility?.blocked_collections?.length || rd.eligibility?.blocked_placements?.length) ? (
        <section className="bg-white rounded-2xl border border-line p-4" data-testid="catalog-eligibility-blocks">
          <h3 className="font-bold text-ink mb-2 flex items-center gap-2"><AlertTriangle size={16} className="text-brand" /> Eligibility blocks (server-side)</h3>
          <ul className="text-xs space-y-1.5 text-ink-soft">
            {rd.eligibility.blocked_collections.map((b) => <li key={b.id} data-testid={`catalog-blocked-collection-${b.id}`}><span className="font-semibold text-ink">{b.name}</span> — {b.block_reason}</li>)}
            {rd.eligibility.blocked_placements.map((b) => <li key={b.id}><span className="font-semibold text-ink">{b.name}</span> ({b.sku} · {b.family_member}) — {b.eligibility_block}</li>)}
          </ul>
        </section>
      ) : null}

      <section>
        <h3 className="font-bold text-ink mb-2 flex items-center gap-2"><Link2 size={16} className="text-brand" /> Retailer feed adapters</h3>
        <Adapters adapters={rd.adapters} />
      </section>

      <section>
        <h3 className="font-bold text-ink mb-2 flex items-center gap-2"><Upload size={16} className="text-brand" /> CSV / feed import</h3>
        <CsvImport onDone={load} />
      </section>

      {imports.length > 0 && (
        <section className="bg-white rounded-2xl border border-line p-4" data-testid="catalog-imports-list">
          <h3 className="font-bold text-ink mb-2">Imported drafts ({imports.length}) — all hidden &amp; unapproved</h3>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {imports.map((d) => (
              <div key={d.id} className="flex flex-wrap items-start justify-between gap-2 text-xs border-t border-line pt-2" data-testid={`catalog-draft-${d.id}`}>
                <div><p className="font-semibold text-ink">{d.name}</p><p className="text-ink-muted">{d.retailer} · {d.sku || "no SKU"} · {d.occasion || "no occasion"}</p></div>
                <div className="flex flex-wrap gap-1 max-w-[60%] justify-end">
                  {d.import_ready ? <span className="px-2 py-0.5 rounded-full bg-success/10 text-success font-bold">Ready for admin review</span>
                    : (d.import_blocks || []).map((b) => <span key={b.code} title={b.detail} className="px-2 py-0.5 rounded-full bg-cream border border-line capitalize">{fmtCode(b.code)}</span>)}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-line p-4" data-testid="catalog-chart-gaps">
          <h3 className="font-bold text-ink mb-2 flex items-center gap-2"><Ruler size={16} className="text-brand" /> Size-chart gaps</h3>
          <ul className="text-xs space-y-1.5">
            {rd.chart_gaps.map((g, i) => (
              <li key={`${g.brand}-${i}`} className="flex items-start gap-2">
                {g.has_any_approved_chart ? <CheckCircle2 size={13} className="text-success mt-0.5 shrink-0" /> : <AlertTriangle size={13} className="text-brand mt-0.5 shrink-0" />}
                <span><span className="font-semibold text-ink">{g.brand}</span>{" — "}
                  {g.has_any_approved_chart ? `approved: ${g.approved_clothing_types.join(", ")}` : "no approved chart"}
                  {g.missing_for_catalog?.length ? ` · missing: ${g.missing_for_catalog.join(", ")}` : ""}
                  {g.note ? ` · ${g.note}` : ""}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-white rounded-2xl border border-line p-4" data-testid="catalog-data-required">
          <h3 className="font-bold text-ink mb-2">Data still required</h3>
          <ul className="text-xs text-ink-soft list-disc pl-4 space-y-1.5">{rd.data_still_required.map((t, i) => <li key={i}>{t}</li>)}</ul>
        </div>
      </section>
      <SleepwearReadiness />
    </div>
  );
}
